import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import PhotoIcon from "@mui/icons-material/Photo";

import ShareIcon from "@mui/icons-material/Share";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, analytics } from "common/analytics";
import {
  AddFertilizerEventDialog,
  AddFieldPreparationEventDialog,
  AddReseedEventDialog,
} from "common/components/AddNewEventDialogs";
import type { FileItem } from "common/components/FileItemList";
import FileItemList from "common/components/FileItemList";
import { downloadFileHelper } from "features/fileManagement/downloadFileHelper";
import type {
  AutoEvidenceItem,
  EventFileEntry,
  EvidenceType,
  FertilizerAutoEvidenceItem,
  FieldPreparationAutoEvidenceItem,
  ReseedingAutoEvidenceItem,
} from "model";
import {
  FILE_UPLOAD_TYPE,
  useActionEventEvidence,
  useActionEvidence,
  useCreateActionFileEntry,
  useDeleteActionFileEntry,
  useEventFilesByEvents,
  useFertilizerAutoEvidence,
  useFieldPreparationAutoEvidence,
  useReseedingAutoEvidence,
  useShareEventAsEvidence,
  useUnshareEventAsEvidence,
} from "model";
import { useMemo, useState } from "react";
import { useAppStore } from "stores";
import ActionEvidenceUpload from "./ActionEvidenceUpload";
import EvidenceViewerDialog from "./EvidenceViewerDialog";

interface Props {
  evidenceType: EvidenceType;
  actionSubmissionGuid: string;
  requiredEvidence: string;
  userGuid: string;
  readOnly?: boolean;
}

function formatEnumLabel(value: string | undefined): string {
  if (!value) return "";
  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isReseedEvent(
  event: AutoEvidenceItem
): event is ReseedingAutoEvidenceItem {
  return "reseedType" in event;
}

function isFertilizerEvent(
  event: AutoEvidenceItem
): event is FertilizerAutoEvidenceItem {
  return "fertilizerProperties" in event;
}

function isFieldPreparationEvent(
  event: AutoEvidenceItem
): event is FieldPreparationAutoEvidenceItem {
  return (
    "machinery" in event &&
    !("reseedType" in event) &&
    !("fertilizerProperties" in event)
  );
}

// --- Event card components ---

function getEventTypeLabel(event: AutoEvidenceItem): string {
  if (isReseedEvent(event)) {
    return event.reseedType === "OVERSEED" ? "Overseed" : "Reseed";
  }
  if (isFertilizerEvent(event)) return "Fertiliser";
  if (isFieldPreparationEvent(event)) return "Field Prep";
  return "";
}

function EventCardFarmer({ event }: { event: AutoEvidenceItem }) {
  const dateStr = new Date(event.date).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return (
    <>
      <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
        {event.fieldNames.join(", ")}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "12px", color: "#71717a" }}>
        {dateStr} {"\u2022"} {getEventTypeLabel(event)}
      </Typography>
    </>
  );
}

function EventCardCorporate({ event }: { event: AutoEvidenceItem }) {
  const detail = (label: string, value: string | undefined | null) =>
    value ? (
      <Typography component="div" variant="body2" sx={{ fontSize: "13px" }}>
        <strong>{label}:</strong> {value}
      </Typography>
    ) : null;

  const title = isReseedEvent(event)
    ? "Seeding"
    : isFertilizerEvent(event)
    ? "Fertiliser Application"
    : "Field Preparation";
  const dateStr = new Date(event.date).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const areaLabel = event.areaHa ? `${event.areaHa} ha` : "";

  return (
    <Box>
      <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      {detail("Date", dateStr)}
      {detail("Area", areaLabel)}
      {isReseedEvent(event) && (
        <>
          {detail(
            "New grass type",
            event.newGrassType ? formatEnumLabel(event.newGrassType) : undefined
          )}
          {detail(
            "Seeding method",
            event.seedingMethod
              ? formatEnumLabel(event.seedingMethod)
              : undefined
          )}
          {detail("Seed mix", event.productName || undefined)}
          {detail(
            "Seed rate",
            event.seedingRate ? `${event.seedingRate} kg/ha` : undefined
          )}
        </>
      )}
      {isFertilizerEvent(event) && (
        <>
          {detail(
            "Type",
            formatEnumLabel(event.fertilizerProperties.fertilizerType)
          )}
          {detail(
            "Amount",
            `${event.totalAmount} ${event.totalAmountUnit?.toLowerCase() ?? ""}`
          )}
          {detail(
            "Product",
            event.fertilizerProperties.productName || undefined
          )}
          {detail("Note", event.note || undefined)}
        </>
      )}
      {isFieldPreparationEvent(event) && (
        <>
          {detail("Fuel", event.fuel ? `${event.fuel} litre` : undefined)}
          {detail(
            "Machinery",
            event.machinery?.map(formatEnumLabel).join(", ") || undefined
          )}
          {detail("Note", event.note || undefined)}
        </>
      )}
    </Box>
  );
}

function EventCard({
  event,
  readOnly = false,
}: {
  event: AutoEvidenceItem;
  readOnly?: boolean;
}) {
  if (readOnly) return <EventCardCorporate event={event} />;
  return <EventCardFarmer event={event} />;
}

// --- File row ---

function EventFileRow({
  file,
  isShared,
  readOnly,
  isMutating,
  onShare,
  onUnshare,
  onPreview,
  onDownload,
}: {
  file: EventFileEntry;
  isShared: boolean;
  readOnly: boolean;
  isMutating: boolean;
  onShare: () => void;
  onUnshare: () => void;
  onPreview: () => void;
  onDownload: () => Promise<void>;
}) {
  const isPhoto =
    file.fileUploadType === FILE_UPLOAD_TYPE.ACTION_EVIDENCE_PHOTO.valueOf();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid #F0F0F0",
        bgcolor: "white",
        borderRadius: "6px",
        px: 1.5,
        py: 0.5,
        mb: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {isPhoto ? (
          <PhotoIcon sx={{ color: "#2E7831", fontSize: 18, flexShrink: 0 }} />
        ) : (
          <InsertDriveFileIcon
            sx={{ color: "#7D5DA3", fontSize: 18, flexShrink: 0 }}
          />
        )}
        <Typography
          sx={{
            fontSize: "13px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {file.fileName || "Unnamed file"}
        </Typography>
      </Box>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}
      >
        <IconButton size="small" onClick={onPreview}>
          <VisibilityIcon sx={{ fontSize: 16 }} />
        </IconButton>
        {!readOnly && (
          <IconButton size="small" onClick={onDownload}>
            <DownloadIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {!readOnly &&
          (isShared ? (
            <Button
              size="small"
              startIcon={<LinkOffIcon sx={{ fontSize: 14 }} />}
              onClick={onUnshare}
              disabled={isMutating}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                color: "#71717a",
                minWidth: "auto",
                "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" },
              }}
            >
              Unshare file
            </Button>
          ) : (
            <Button
              size="small"
              startIcon={<ShareIcon sx={{ fontSize: 14 }} />}
              onClick={onShare}
              disabled={isMutating}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                color: "#2E7831",
                minWidth: "auto",
              }}
            >
              Share file
            </Button>
          ))}
      </Box>
    </Box>
  );
}

// --- Share evidence dialog ---

function ShareEvidenceDialog({
  open,
  event,
  files,
  isMutating,
  onConfirm,
  onClose,
  onPreview,
}: {
  open: boolean;
  event: AutoEvidenceItem;
  files: EventFileEntry[];
  isMutating: boolean;
  onConfirm: (selectedFileGuids: string[]) => void;
  onClose: () => void;
  onPreview: (file: EventFileEntry) => void;
}) {
  const [selectedGuids, setSelectedGuids] = useState<Set<string>>(
    () => new Set(files.map((f) => f.fileEntryGuid))
  );

  const toggleFile = (fileEntryGuid: string) => {
    setSelectedGuids((prev) => {
      const next = new Set(prev);
      if (next.has(fileEntryGuid)) {
        next.delete(fileEntryGuid);
      } else {
        next.add(fileEntryGuid);
      }
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: "16px", fontWeight: 600 }}>
        Share as Evidence
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{ fontSize: "13px", color: "#71717a", mb: 2 }}
        >
          This event and any selected files will be shared with your programme
          manager as evidence for this action.
        </Typography>
        <Box
          sx={{
            bgcolor: "#f4f4f5",
            borderRadius: "6px",
            p: 1.5,
            mb: 2,
          }}
        >
          <EventCard event={event} />
        </Box>
        {files.length > 0 && (
          <>
            <Typography
              variant="body2"
              sx={{ fontSize: "13px", color: "#71717a", mb: 1 }}
            >
              Select files to include:
            </Typography>
            {files.map((file) => (
              <Box
                key={file.guid}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedGuids.has(file.fileEntryGuid)}
                      onChange={() => toggleFile(file.fileEntryGuid)}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: "13px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.fileName || "Unnamed file"}
                    </Typography>
                  }
                  sx={{ flex: 1, minWidth: 0, mr: 0 }}
                />
                <IconButton
                  size="small"
                  onClick={() => onPreview(file)}
                  sx={{ flexShrink: 0 }}
                >
                  <VisibilityIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(Array.from(selectedGuids))}
          disabled={isMutating}
          sx={{
            textTransform: "none",
            bgcolor: "#2E7831",
            "&:hover": { bgcolor: "#1C4321" },
          }}
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Available event card ---

function AvailableEventCard({
  event,
  files,
  borderColor,
  isMutating,
  onShare,
  onPreviewFile,
}: {
  event: AutoEvidenceItem;
  files: EventFileEntry[];
  borderColor: string;
  isMutating: boolean;
  onShare: (selectedFileGuids: string[]) => void;
  onPreviewFile: (file: EventFileEntry) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleClick = () => {
    if (files.length > 0) {
      setDialogOpen(true);
    } else {
      onShare([]);
    }
  };

  return (
    <>
      <Box
        sx={{
          border: "1px solid #F0F0F0",
          borderRadius: "8px",
          p: 1.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EventCard event={event} />
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={handleClick}
          disabled={isMutating}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            borderColor: "#2E7831",
            color: "#2E7831",
            "&:hover": { borderColor: "#1C4321", bgcolor: "#F7FCF1" },
            flexShrink: 0,
            ml: 1,
          }}
        >
          Share as Evidence
        </Button>
      </Box>
      {files.length > 0 && (
        <ShareEvidenceDialog
          open={dialogOpen}
          event={event}
          files={files}
          isMutating={isMutating}
          onConfirm={(selectedFileGuids) => {
            onShare(selectedFileGuids);
            setDialogOpen(false);
          }}
          onClose={() => setDialogOpen(false)}
          onPreview={onPreviewFile}
        />
      )}
    </>
  );
}

// --- Main component ---

export default function UnifiedEvidenceList({
  evidenceType,
  actionSubmissionGuid,
  requiredEvidence,
  userGuid,
  readOnly = false,
}: Props) {
  const user = useAppStore((state) => state.user);
  const { notify } = useNotification();
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    fileEntryGuid: string;
    fileName: string;
  } | null>(null);

  // Fetch events (corporate passes actionSubmissionGuid for server-side filtering)
  const corpActionGuid = readOnly ? actionSubmissionGuid : undefined;
  const { data: reseedEvents = [], isLoading: reseedLoading } =
    useReseedingAutoEvidence(
      evidenceType === "reseeding" ? userGuid : undefined,
      corpActionGuid
    );
  const { data: fertilizerEvents = [], isLoading: fertilizerLoading } =
    useFertilizerAutoEvidence(
      evidenceType === "fertilizer" ? userGuid : undefined,
      corpActionGuid
    );
  const { data: fieldPrepEvents = [], isLoading: fieldPrepLoading } =
    useFieldPreparationAutoEvidence(
      evidenceType === "fieldPreparation" ? userGuid : undefined,
      corpActionGuid
    );

  const events: AutoEvidenceItem[] =
    evidenceType === "reseeding"
      ? reseedEvents
      : evidenceType === "fertilizer"
      ? fertilizerEvents
      : fieldPrepEvents;
  const eventsLoading =
    evidenceType === "reseeding"
      ? reseedLoading
      : evidenceType === "fertilizer"
      ? fertilizerLoading
      : fieldPrepLoading;

  // Shared events
  const { data: sharedEvidence = [] } =
    useActionEventEvidence(actionSubmissionGuid);
  const sharedEventGuids = useMemo(
    () => new Set(sharedEvidence.map((e) => e.farmEventGuid)),
    [sharedEvidence]
  );

  // Event files
  const eventGuids = events.map((e) => e.guid);
  const { data: eventFiles = [] } = useEventFilesByEvents(
    eventGuids,
    readOnly ? actionSubmissionGuid : undefined
  );
  const filesByEvent = useMemo(() => {
    const map = new Map<string, EventFileEntry[]>();
    for (const file of eventFiles) {
      const existing = map.get(file.farmEventGuid) || [];
      existing.push(file);
      map.set(file.farmEventGuid, existing);
    }
    return map;
  }, [eventFiles]);

  // Action evidence (per-file sharing)
  const { data: actionEvidence = [], isLoading: evidenceLoading } =
    useActionEvidence(actionSubmissionGuid);
  const sharedFileEntryGuids = useMemo(
    () => new Set(actionEvidence.map((e) => e.fileEntryGuid)),
    [actionEvidence]
  );
  const eventFileEntryGuids = useMemo(
    () => new Set(eventFiles.map((f) => f.fileEntryGuid)),
    [eventFiles]
  );

  // Mutations
  const createActionFileEntry = useCreateActionFileEntry();
  const deleteActionFileEntry = useDeleteActionFileEntry();
  const shareEventMutation = useShareEventAsEvidence();
  const unshareEventMutation = useUnshareEventAsEvidence();

  const isMutating =
    createActionFileEntry.isPending ||
    deleteActionFileEntry.isPending ||
    shareEventMutation.isPending ||
    unshareEventMutation.isPending;

  // Split events
  const sharedEvents = events
    .filter((e) => sharedEventGuids.has(e.guid))
    .sort((a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf());
  const availableEvents = events
    .filter((e) => !sharedEventGuids.has(e.guid))
    .sort((a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf());

  // Direct uploads
  const directUploads = useMemo(() => {
    return actionEvidence
      .filter((e) => !eventFileEntryGuids.has(e.fileEntryGuid))
      .map(
        (e) =>
          ({
            guid: e.guid,
            fileEntryGuid: e.fileEntryGuid,
            fileName: e.fileName,
            fileUploadType: e.fileUploadType,
            creationDate: e.creationDate,
            canDelete: e.uploaderGuid === user?.guid,
          } as FileItem)
      );
  }, [actionEvidence, eventFileEntryGuids, user?.guid]);

  // Handlers
  const handleShareFile = async (file: EventFileEntry) => {
    if (!user?.guid) return;
    await createActionFileEntry.mutateAsync({
      fileEntryGuid: file.fileEntryGuid,
      actionSubmissionGuid,
      fileName: file.fileName,
      fileUploadType: file.fileUploadType,
      fileEntryFileUploadGuid: file.fileEntryFileUploadGuid,
      uploaderGuid: user.guid,
    });
    analytics.track(AnalyticsEvents.EVIDENCE_FILE_SHARED, {
      evidenceType,
      actionSubmissionGuid,
    });
  };

  const handleUnshareFile = async (fileEntryGuid: string) => {
    const entry = actionEvidence.find((e) => e.fileEntryGuid === fileEntryGuid);
    if (!entry) return;
    await deleteActionFileEntry.mutateAsync({
      guid: entry.guid,
      actionSubmissionGuid,
    });
    analytics.track(AnalyticsEvents.EVIDENCE_FILE_UNSHARED, {
      evidenceType,
      actionSubmissionGuid,
    });
  };

  const handleShareEvent = async (
    eventGuid: string,
    selectedFileGuids: string[]
  ) => {
    await shareEventMutation.mutateAsync({
      actionSubmissionGuid,
      farmEventGuid: eventGuid,
    });
    if (user?.guid && selectedFileGuids.length > 0) {
      const files = filesByEvent.get(eventGuid) || [];
      const selectedSet = new Set(selectedFileGuids);
      for (const file of files) {
        if (
          selectedSet.has(file.fileEntryGuid) &&
          !sharedFileEntryGuids.has(file.fileEntryGuid)
        ) {
          await createActionFileEntry.mutateAsync({
            fileEntryGuid: file.fileEntryGuid,
            actionSubmissionGuid,
            fileName: file.fileName,
            fileUploadType: file.fileUploadType,
            fileEntryFileUploadGuid: file.fileEntryFileUploadGuid,
            uploaderGuid: user.guid,
          });
        }
      }
    }
    analytics.track(AnalyticsEvents.EVIDENCE_EVENT_SHARED, {
      evidenceType,
      actionSubmissionGuid,
      farmEventGuid: eventGuid,
      fileCount: selectedFileGuids.length,
    });
  };

  const handleUnshareEvent = async (eventGuid: string) => {
    const entry = sharedEvidence.find((e) => e.farmEventGuid === eventGuid);
    if (!entry) return;
    await unshareEventMutation.mutateAsync({
      guid: entry.guid,
      actionSubmissionGuid,
    });
    analytics.track(AnalyticsEvents.EVIDENCE_EVENT_UNSHARED, {
      evidenceType,
      actionSubmissionGuid,
    });
  };

  const handleDeleteDirectUpload = async (guid: string) => {
    await deleteActionFileEntry.mutateAsync({ guid, actionSubmissionGuid });
    analytics.track(AnalyticsEvents.EVIDENCE_DIRECT_UPLOAD_DELETED, {
      evidenceType,
      actionSubmissionGuid,
    });
  };

  const labels: Record<EvidenceType, string> = {
    reseeding: "Add Seeding Event",
    fertilizer: "Add Fertiliser Event",
    fieldPreparation: "Add Field Prep Event",
  };
  const addEventLabel = labels[evidenceType];
  const eventBorderColor = "#DBF4C2";
  const eventBgColor = "#F7FCF1";

  if (eventsLoading || evidenceLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Upload buttons + Add Event (farmer only) */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <ActionEvidenceUpload
          actionSubmissionGuid={actionSubmissionGuid}
          requiredEvidence={requiredEvidence}
          evidenceType={evidenceType}
        />
        {!readOnly && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddEventDialogOpen(true)}
            sx={{
              textTransform: "none",
              borderColor: "#2E7831",
              color: "#2E7831",
              "&:hover": { borderColor: "#1C4321", bgcolor: "#F7FCF1" },
            }}
          >
            {addEventLabel}
          </Button>
        )}
      </Box>

      {/* Direct uploads */}
      {directUploads.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <FileItemList
            files={directUploads}
            onDelete={handleDeleteDirectUpload}
            isDeletePending={deleteActionFileEntry.isPending}
          />
        </Box>
      )}

      {/* Section 1: Shared Evidence */}
      {sharedEvents.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {sharedEvents.map((event) => {
              const files = filesByEvent.get(event.guid) || [];
              const visibleFiles = readOnly
                ? files.filter((f) => sharedFileEntryGuids.has(f.fileEntryGuid))
                : files;
              return (
                <Box
                  key={event.guid}
                  sx={{
                    border: "1px solid #F0F0F0",
                    borderRadius: "8px",
                    bgcolor: "white",
                    p: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <EventCard event={event} readOnly={readOnly} />
                    </Box>
                    {!readOnly && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleUnshareEvent(event.guid)}
                        disabled={isMutating}
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          color: "#dc2626",
                          borderColor: "#fecaca",
                          flexShrink: 0,
                          ml: 1,
                          "&:hover": {
                            borderColor: "#dc2626",
                            bgcolor: "#fef2f2",
                          },
                        }}
                      >
                        Unshare event
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ mt: visibleFiles.length > 0 ? 1 : 0 }}>
                    {visibleFiles.map((file) => (
                      <EventFileRow
                        key={file.guid}
                        file={file}
                        isShared={sharedFileEntryGuids.has(file.fileEntryGuid)}
                        readOnly={readOnly}
                        isMutating={isMutating}
                        onShare={() => handleShareFile(file)}
                        onUnshare={() => handleUnshareFile(file.fileEntryGuid)}
                        onPreview={() => {
                          setPreviewFile({
                            fileEntryGuid: file.fileEntryGuid,
                            fileName: file.fileName,
                          });
                          analytics.track(
                            AnalyticsEvents.EVIDENCE_FILE_PREVIEWED,
                            { evidenceType, actionSubmissionGuid }
                          );
                        }}
                        onDownload={async () => {
                          await downloadFileHelper(file.fileEntryGuid, notify);
                          analytics.track(
                            AnalyticsEvents.EVIDENCE_FILE_DOWNLOADED,
                            { evidenceType, actionSubmissionGuid }
                          );
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Section 2: Available Events (farmer only) */}
      {!readOnly && availableEvents.length > 0 && (
        <Box>
          <Typography
            variant="body2"
            sx={{ fontSize: "13px", color: "#71717a", mb: 1 }}
          >
            Events that could be used as evidence for this action
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {availableEvents.map((event) => (
              <AvailableEventCard
                key={event.guid}
                event={event}
                files={filesByEvent.get(event.guid) || []}
                borderColor={eventBorderColor}
                isMutating={isMutating}
                onShare={(selectedFileGuids) =>
                  handleShareEvent(event.guid, selectedFileGuids)
                }
                onPreviewFile={(file) => {
                  setPreviewFile({
                    fileEntryGuid: file.fileEntryGuid,
                    fileName: file.fileName,
                  });
                  analytics.track(AnalyticsEvents.EVIDENCE_FILE_PREVIEWED, {
                    evidenceType,
                    actionSubmissionGuid,
                  });
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Empty state */}
      {sharedEvents.length === 0 &&
        directUploads.length === 0 &&
        (readOnly || availableEvents.length === 0) && (
          <Typography
            variant="body2"
            sx={{ color: "#71717a", fontSize: "14px" }}
          >
            No evidence yet.
          </Typography>
        )}

      {/* Dialogs */}
      {evidenceType === "reseeding" && (
        <AddReseedEventDialog
          dialogOpen={addEventDialogOpen}
          setDialogOpen={setAddEventDialogOpen}
        />
      )}
      {evidenceType === "fertilizer" && (
        <AddFertilizerEventDialog
          dialogOpen={addEventDialogOpen}
          setDialogOpen={setAddEventDialogOpen}
        />
      )}
      {evidenceType === "fieldPreparation" && (
        <AddFieldPreparationEventDialog
          dialogOpen={addEventDialogOpen}
          setDialogOpen={setAddEventDialogOpen}
        />
      )}
      <EvidenceViewerDialog
        open={!!previewFile}
        fileEntryGuid={previewFile?.fileEntryGuid ?? ""}
        fileName={previewFile?.fileName ?? ""}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
}
